import { useCallback, useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { notifyOk, notifyError } from "@/lib/notify";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Field } from "@/components/forms/fields";
import { AlertTriangle, ChevronDown, ChevronUp, Eye, EyeOff, Loader2, ShieldCheck } from "lucide-react";

export const BLOCKER_LABEL = {
  sitios: "Sitios que pertenecen a esta organización",
  usuarios: "Usuarios ligados a esta organización",
  usuarios_con_acceso: "Usuarios con este sitio autorizado",
  personas: "Personas dadas de alta",
  dispositivos: "Dispositivos registrados",
  zonas: "Zonas configuradas",
  camas: "Camas configuradas",
  puentes: "Puentes de ingesta",
  cuidadoras: "Cuidadoras registradas",
  casos: "Casos / alarmas en el historial",
  eventos: "Eventos recibidos",
  recordatorios: "Recordatorios de alta",
  pendientes_operativos: "Pendientes operativos",
  configuracion_de_sitio: "Configuración de monitoreo",
  planes_respuesta: "Planes de respuesta",
  gestion_de_contacto: "Gestiones de contacto registradas",
  historial_de_casos: "Historial de acciones de casos",
  bitacora: "Movimientos en la bitácora",
  casos_como_operador: "Casos que atendió como operador",
  transiciones_de_caso: "Cambios de estado de casos hechos por esta persona",
  acciones_de_caso: "Acciones registradas en casos",
  waits_creadas: "Compromisos de espera creados",
  sesiones_iniciadas: "Sesiones de operación iniciadas",
  login_registrado: "Inicios de sesión registrados",
  bitacora_autoria: "Cambios firmados en la bitácora",
  verificaciones_expediente: "Verificaciones de expediente firmadas",
  registros_creados: "Registros creados por esta persona",
  registros_editados: "Registros editados por esta persona",
};

const REASONS = [
  ["created_by_error", "Se creó por error"],
  ["demo_cleanup", "Limpieza de datos de prueba"],
  ["duplicate", "Registro duplicado"],
];

/**
 * Zona de peligro: hard-delete como EXCEPCIÓN. El backend es la autoridad (preflight + revalidación
 * en el momento destructivo). Aquí sólo se presenta la elegibilidad y se recoge la confirmación.
 */
export default function ZonaPeligro({ basePath, tipoLabel, confirmarCon, confirmarValor,
  testPrefix, onDeleted, labelToggle }) {
  const [abierta, setAbierta] = useState(false);
  const [pre, setPre] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [reason, setReason] = useState("");
  const [password, setPassword] = useState("");
  const [verPass, setVerPass] = useState(false);
  const [texto, setTexto] = useState("");
  const [error, setError] = useState("");
  const [borrando, setBorrando] = useState(false);

  const preflight = useCallback(() => {
    setCargando(true);
    api.post(`${basePath}/delete-preflight`)
      .then((r) => setPre(r.data))
      .catch((e) => notifyError(formatApiErrorDetail(e.response?.data?.detail)))
      .finally(() => setCargando(false));
  }, [basePath]);

  useEffect(() => { if (abierta) preflight(); }, [abierta, preflight]);

  const esperado = confirmarValor || pre?.resource_display || "";
  const coincide = texto.trim().toLowerCase() === esperado.trim().toLowerCase() && !!esperado;

  const eliminar = async () => {
    setError("");
    if (!reason) return setError("Selecciona el motivo.");
    if (!password) return setError("Escribe tu contraseña actual.");
    if (!texto.trim()) return setError(`Escribe ${confirmarCon} para confirmar.`);
    setBorrando(true);
    try {
      await api.post(`${basePath}/hard-delete`, {
        current_password: password, confirmation_text: texto, reason_code: reason,
      });
      notifyOk(`Se eliminó permanentemente el registro.`);
      onDeleted?.();
    } catch (e) {
      const d = e.response?.data?.detail;
      if (d?.blockers) { setPre({ ...(pre || {}), can_delete: false, blockers: d.blockers }); }
      setError(formatApiErrorDetail(d));
    } finally {
      setBorrando(false);
      setPassword("");
    }
  };

  return (
    <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden"
      data-testid={`${testPrefix}-zona-peligro`}>
      <button type="button" onClick={() => setAbierta((v) => !v)}
        data-testid={`${testPrefix}-zona-peligro-toggle`}
        className="w-full flex items-center justify-between gap-3 px-5 py-3.5 text-left hover:bg-slate-50 transition-colors">
        <span className="flex items-center gap-2 text-sm font-medium text-slate-600">
          <AlertTriangle className="h-4 w-4 text-slate-400" />
          {labelToggle || "Zona de peligro"}
        </span>
        {abierta ? <ChevronUp className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
      </button>

      {abierta && (
        <div className="border-t border-slate-200 px-5 py-5 space-y-4">
          <p className="text-xs text-slate-500">
            Desactivar es el camino normal y conserva el historial. Eliminar permanentemente sólo
            existe para registros sin historia. <span className="font-medium text-slate-700">Esta
            acción no se puede deshacer.</span>
          </p>

          {cargando && (
            <p className="text-sm text-slate-400 flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" /> Revisando si se puede eliminar…
            </p>
          )}

          {!cargando && pre && !pre.can_delete && (
            <div data-testid={`${testPrefix}-no-elegible`} className="rounded-xl bg-slate-50 border border-slate-200 p-4">
              <p className="text-sm font-medium text-slate-700 flex items-center gap-2">
                <ShieldCheck className="h-4 w-4 text-slate-400" />
                No se puede eliminar: tiene información asociada.
              </p>
              <ul className="mt-2 space-y-1 text-xs text-slate-500">
                {(pre.blockers || []).map((b) => (
                  <li key={b.code} data-testid={`${testPrefix}-blocker-${b.code}`}>
                    · {BLOCKER_LABEL[b.code] || b.code}: <span className="font-medium text-slate-700">{b.count}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-3 text-xs text-slate-500">Desactívalo para retirar el acceso sin perder el historial.</p>
            </div>
          )}

          {!cargando && pre?.can_delete && (
            <div className="space-y-4" data-testid={`${testPrefix}-elegible`}>
              <p className="text-sm text-slate-600">
                Sin historia asociada: se puede eliminar permanentemente.
              </p>
              <Field label="Motivo">
                <Select value={reason} onValueChange={setReason}>
                  <SelectTrigger className="rounded-xl" data-testid={`${testPrefix}-reason-select`}>
                    <SelectValue placeholder="Selecciona el motivo" />
                  </SelectTrigger>
                  <SelectContent>
                    {REASONS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Tu contraseña actual">
                <div className="relative">
                  <Input type={verPass ? "text" : "password"} className="rounded-xl pr-10"
                    value={password} onChange={(e) => setPassword(e.target.value)}
                    data-testid={`${testPrefix}-password-input`} autoComplete="current-password" />
                  <button type="button" onClick={() => setVerPass((v) => !v)}
                    data-testid={`${testPrefix}-password-toggle`}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                    {verPass ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </Field>
              <Field label={`Escribe ${confirmarCon} para confirmar`}>
                <Input className="rounded-xl" value={texto} onChange={(e) => setTexto(e.target.value)}
                  placeholder={confirmarValor || pre.resource_display}
                  data-testid={`${testPrefix}-confirm-input`} />
              </Field>
              {error && (
                <p data-testid={`${testPrefix}-error`} className="text-xs text-rose-600">{error}</p>
              )}
              <Button variant="outline" disabled={borrando || !reason || !password || !coincide}
                onClick={eliminar} data-testid={`${testPrefix}-eliminar-button`}
                className="rounded-xl border-rose-300 text-rose-700 hover:bg-rose-50">
                {borrando ? "Eliminando…" : `Eliminar ${tipoLabel} permanentemente`}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
